import React, { useState, useEffect, useCallback } from 'react';
import { C } from '../constants/theme';
import { Table } from '../components/Table';
import { Pagination } from '../components/Pagination';
import { PageHeader } from '../components/PageHeader';
import { SearchInput } from '../components/SearchInput';
import { Modal } from '../components/Modal';
import { FormField, inputStyle, selectStyle } from '../components/FormField';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { getViewTypes, createViewType, updateViewType } from '../utils/api';

const cardStyle = {
  background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
  boxShadow: '0 1px 4px rgba(0,0,0,.06)', overflow: 'hidden',
};

const INIT_FORM = { viewType: '', viewDescription: '', status: 1 };

export function ViewTypeModule() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(INIT_FORM);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getViewTypes({ pageNumber: page, pageSize });
      const obj = res.responseObject;
      const filtered = search
        ? obj.content.filter((r) =>
            r.viewType.toLowerCase().includes(search.toLowerCase()) ||
            (r.viewDescription || '').toLowerCase().includes(search.toLowerCase())
          )
        : obj.content;
      setData(filtered);
      setTotal(search ? filtered.length : obj.totalElements);
      setTotalPages(search ? Math.ceil(filtered.length / pageSize) : obj.totalPages);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => { setForm(INIT_FORM); setModal('add'); };
  const openEdit = (row) => {
    setForm({ viewType: row.viewType, viewDescription: row.viewDescription, status: row.status });
    setModal(row);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal === 'add') await createViewType(form);
      else await updateViewType(modal.id, form);
      setModal(null);
      fetchData();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'viewType', label: 'View Type' },
    { key: 'viewDescription', label: 'Description', wrap: true },
    { key: 'status', label: 'Status', render: (v) => <Badge status={v} /> },
  ];

  return (
    <div>
      <PageHeader title="View Type" count={total} onAdd={openAdd} />
      <div style={cardStyle}>
        <div style={{ display: 'flex', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${C.border}` }}>
          <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(0); }} placeholder="Search view type…" />
        </div>
        {loading ? <LoadingSpinner /> : (
          <Table columns={columns} data={data} actions={(row) => [
            <Button key="e" small variant="outline" onClick={() => openEdit(row)}>Edit</Button>,
          ]} />
        )}
        <Pagination
          page={page} totalPages={totalPages} totalElements={total} pageSize={pageSize}
          onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
        />
      </div>

      {modal && (
        <Modal title={modal === 'add' ? 'Add View Type' : 'Edit View Type'} onClose={() => setModal(null)}>
          <div style={{ display: 'grid', gap: 18 }}>
            <FormField label="View Type" required>
              <input style={inputStyle} value={form.viewType} autoFocus
                onChange={(e) => setForm((f) => ({ ...f, viewType: e.target.value }))}
                placeholder="e.g. CARD_HORIZONTAL" />
            </FormField>
            <FormField label="Description">
              <textarea style={{ ...inputStyle, height: 90, resize: 'vertical' }}
                value={form.viewDescription}
                onChange={(e) => setForm((f) => ({ ...f, viewDescription: e.target.value }))}
                placeholder="Describe this view type…" />
            </FormField>
            <FormField label="Status">
              <select style={selectStyle} value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: Number(e.target.value) }))}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
            <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!form.viewType || saving}>
              {saving ? 'Saving…' : modal === 'add' ? 'Create' : 'Update'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
